import { http } from '../../http';
import { __ } from '@/js/stores/expressions';
import { useCustomConfig } from '@/js/composables/useCustomAxiosRequestConfig';
import type {
    RequestMigrateCompany,
    RequestMigrateSystem,
    ResponseMigrateCompany,
    ResponseGetUnmigratedCompanies,
    ResponseGetUnmigratedExternalSystems,
    ResponseGetMigratedExternalSystems,
    ResponseGetMigratedTenants,
    ResponseGetTenants,
    DataSeedingResponse,
    ApiCompany,
    KeyIndicateUserWithEchoMatch,
    ApiRole,
    RequestImportUsers,
    ApiKeyIndicateUsersResponse,
    ApiTenantRolesResponse,
    ResponseGetImportTenantConfigOptions,
    RequestImportTenantConfig,
    RequestUpdateOrgTree,
    ResponseUpdateOrgTree,
    ResponseGetImportIntegrationConfigOptions,
    RequestImportIntegrationConfig,
    ResponseGetIntegrationConfigOptions,
    IntegrationConfigValues,
    RequestSaveIntegrationConfig,
    ResponseGetIntegrationConfigs,
    RequestCreateTenant,
    ResponseCreateTenant,
    TenantExternalSystem,
    RequestSyncRawDataProcessor,
    RequestSyncCoachingData,
    RequestSyncAiInsightsData,
    SyncJob,
    ResponseGetIntegrationOverview,
    RequestManualSync,
    ManualSyncResult,
    ResponseGetTenantUsers,
    ResponseGetSysOpUsers,
    RequestCreateSysOpUser,
    ApiTenantUser,
    RequestChangeUserRole,
    ApiTenant,
    ApiAgentWithoutUser,
    RequestEnqueueCreateUsersFromAgents,
    ApiCreateUserFromAgentEnqueueResult,
    ApiCreateUserFromAgentJobEntry,
} from './types';

export const getTenants = async (): Promise<ResponseGetTenants> => {
    return http
        .backoffice()
        .get<ResponseGetTenants>(
            '/tenants',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-tenants'),
                    502: __('error-load-tenants'),
                },
            })
        )
        .then((response) => response.data);
};

export const getTenant = async (id: string): Promise<ApiTenant> => {
    return http
        .backoffice()
        .get<ApiTenant>(
            `/tenants/${id}`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-tenants'),
                    502: __('error-load-tenants'),
                },
            })
        )
        .then((response) => response.data);
};

export function updateTenant(data: ApiTenant): Promise<unknown> {
    return http
        .backoffice()
        .put(
            `/tenants/${data.id}`,
            data,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-tenants'),
                    502: __('error-load-tenants'),
                },
            })
        )
        .then((response) => response.data);
}

export const getUnmigratedCompanies = async (): Promise<ResponseGetUnmigratedCompanies> => {
    return http
        .backoffice()
        .get<ResponseGetUnmigratedCompanies>(
            '/key-indicate/unmigrated-companies',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-active-nonimported-companies'),
                    502: __('error-load-active-nonimported-companies'),
                },
            })
        )
        .then((response) => response.data);
};

export const getUnmigratedExternalSystems =
    async (): Promise<ResponseGetUnmigratedExternalSystems> => {
        return http
            .backoffice()
            .get<ResponseGetUnmigratedExternalSystems>(
                '/key-indicate/unmigrated-external-systems',
                useCustomConfig({
                    errorMessage: {
                        500: __('error-load-active-nonimported-external-systems'),
                        502: __('error-load-active-nonimported-external-systems'),
                    },
                })
            )
            .then((response) => response.data);
    };

export const getMigratedTenants = async (): Promise<ResponseGetMigratedTenants> => {
    return http
        .backoffice()
        .get<ResponseGetMigratedTenants>(
            '/key-indicate/migrated-tenants',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-migrated-tenants'),
                    502: __('error-load-migrated-tenants'),
                },
            })
        )
        .then((response) => response.data);
};

export const getMigratedExternalSystems = async (): Promise<ResponseGetMigratedExternalSystems> => {
    return http
        .backoffice()
        .get<ResponseGetMigratedExternalSystems>(
            '/key-indicate/migrated-external-systems',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-migrated-external-systems'),
                    502: __('error-load-migrated-external-systems'),
                },
            })
        )
        .then((response) => response.data);
};

export const migrateCompany = async (
    request: RequestMigrateCompany
): Promise<ResponseMigrateCompany> => {
    return http
        .backoffice()
        .post<ResponseMigrateCompany>(
            '/key-indicate/migrate-company',
            request,
            useCustomConfig({
                errorMessage: {
                    400: __('error-migrate-legacy-tenant'),
                    500: __('error-migrate-legacy-tenant'),
                    502: __('error-migrate-legacy-tenant'),
                },
            })
        )
        .then((response) => response.data);
};

export const migrateSystem = async (request: RequestMigrateSystem): Promise<void> => {
    return http
        .backoffice()
        .post(
            '/key-indicate/migrate-system',
            request,
            useCustomConfig({
                errorMessage: {
                    400: __('error-migrate-legacy-integration'),
                    500: __('error-migrate-legacy-integration'),
                    502: __('error-migrate-legacy-integration'),
                },
            })
        )
        .then(() => undefined);
};

export const getCompanies = async (): Promise<ApiCompany[]> => {
    return http
        .backoffice()
        .get<ApiCompany[]>(
            '/key-indicate/companies',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-companies'),
                    502: __('error-load-companies'),
                },
            })
        )
        .then((response) => response.data);
};

export const getKeyIndicateUsers = async (
    tenantId: string
): Promise<KeyIndicateUserWithEchoMatch[]> => {
    return http
        .backoffice()
        .get<ApiKeyIndicateUsersResponse>(
            '/key-indicate/unmigrated-users',
            useCustomConfig({
                params: { tenantId },
                errorMessage: {
                    500: __('error-load-key-indicate-users'),
                    502: __('error-load-key-indicate-users'),
                },
            })
        )
        .then((response) =>
            response.data.users.map((u) => ({
                keyIndicateUser: u,
                echoRole: u.echoRoleId ? { id: u.echoRoleId, name: '' } : null,
            }))
        );
};

// Excludes SysOp and other hidden/system roles - use this wherever a role is assigned as
// part of a customer's own config (e.g. auto-created agent users). The unfiltered "all
// roles" endpoint (tenant/{tenantName}/roles) has no frontend wrapper on purpose - it's for
// our own internal tooling only and shouldn't be reachable from a dropdown by accident.
export const getAssignableTenantRoles = async (tenantName: string): Promise<ApiRole[]> => {
    return http
        .backoffice()
        .get<ApiTenantRolesResponse>(
            `/tenant/${tenantName}/roles/assignable`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-tenant-roles'),
                    502: __('error-load-tenant-roles'),
                },
            })
        )
        .then((response) => response.data.roles);
};

export const getAgentsWithoutUser = async (tenantId: string): Promise<ApiAgentWithoutUser[]> => {
    return http
        .backoffice()
        .get<ApiAgentWithoutUser[]>(
            `/tenant/users/${tenantId}/agents-without-user`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-agents-without-user'),
                    502: __('error-load-agents-without-user'),
                },
            })
        )
        .then((response) => response.data);
};

export const enqueueCreateUsersFromAgents = async (
    request: RequestEnqueueCreateUsersFromAgents
): Promise<ApiCreateUserFromAgentEnqueueResult[]> => {
    return http
        .backoffice()
        .post<ApiCreateUserFromAgentEnqueueResult[]>(
            `/tenant/users/${request.tenantId}/create-users-from-agents`,
            { agentIds: request.agentIds, roleId: request.roleId },
            useCustomConfig({
                errorMessage: {
                    400: __('error-create-users-from-agents'),
                    500: __('error-create-users-from-agents'),
                    502: __('error-create-users-from-agents'),
                },
            })
        )
        .then((response) => response.data);
};

export const getCreateUserFromAgentJobs = async (
    tenantId: string
): Promise<ApiCreateUserFromAgentJobEntry[]> => {
    return http
        .backoffice()
        .get<ApiCreateUserFromAgentJobEntry[]>(
            `/tenant/users/${tenantId}/create-user-from-agent-jobs`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-create-user-from-agent-jobs'),
                    502: __('error-load-create-user-from-agent-jobs'),
                },
            })
        )
        .then((response) => response.data);
};

export const importUsers = async (request: RequestImportUsers): Promise<void> => {
    return http
        .backoffice()
        .post(
            `/tenant/users/${request.tenantId}/batch`,
            // Migrated V1 users must never get the Keycloak account-setup email
            request.users.map((user) => ({ ...user, sendAccountSetupEmail: false })),
            useCustomConfig({
                errorMessage: {
                    400: __('error-import-agents-from-v1'),
                    500: __('error-import-agents-from-v1'),
                    502: __('error-import-agents-from-v1'),
                },
            })
        )
        .then(() => undefined);
};

export const getTenantConfigOptions = async (
    tenantId?: string
): Promise<ResponseGetImportTenantConfigOptions> => {
    return http
        .backoffice()
        .get<ResponseGetImportTenantConfigOptions>(
            '/key-indicate/sync-config/tenant/options',
            useCustomConfig({
                params: tenantId ? { tenantId } : undefined,
                errorMessage: {
                    500: __('error-get-import-tenant-config-options'),
                    502: __('error-get-import-tenant-config-options'),
                },
            })
        )
        .then((response) => response.data);
};

export const importTenantConfig = async (request: RequestImportTenantConfig): Promise<void> => {
    return http
        .backoffice()
        .post(
            '/key-indicate/sync-config/tenant',
            request,
            useCustomConfig({
                errorMessage: {
                    400: __('error-import-tenant-config'),
                    500: __('error-import-tenant-config'),
                    502: __('error-import-tenant-config'),
                },
            })
        )
        .then(() => undefined);
};

export const getImportIntegrationConfigOptions = async (
    tenantId: string,
    externalSystemId?: string
): Promise<ResponseGetImportIntegrationConfigOptions> => {
    return http
        .backoffice()
        .get<ResponseGetImportIntegrationConfigOptions>(
            '/key-indicate/sync-config/system/options',
            useCustomConfig({
                params: externalSystemId ? { tenantId, systemId: externalSystemId } : { tenantId },
                errorMessage: {
                    500: __('error-get-import-integration-config-options'),
                    502: __('error-get-import-integration-config-options'),
                },
            })
        )
        .then((response) => response.data);
};

export const importIntegrationConfig = async (
    request: RequestImportIntegrationConfig
): Promise<void> => {
    return http
        .backoffice()
        .post(
            '/key-indicate/sync-config/system',
            request,
            useCustomConfig({
                errorMessage: {
                    400: __('error-import-integration-config'),
                    500: __('error-import-integration-config'),
                    502: __('error-import-integration-config'),
                },
            })
        )
        .then(() => undefined);
};

export const getIntegrationConfigOptions =
    async (): Promise<ResponseGetIntegrationConfigOptions> => {
        return http
            .backoffice()
            .get<ResponseGetIntegrationConfigOptions>(
                '/integration-config/options',
                useCustomConfig({
                    errorMessage: {
                        500: __('error-get-integration-config-options'),
                        502: __('error-get-integration-config-options'),
                    },
                })
            )
            .then((response) => response.data);
    };

export const getIntegrationOverview = async (
    systemKey: string
): Promise<ResponseGetIntegrationOverview> => {
    return http
        .backoffice()
        .get<ResponseGetIntegrationOverview>(
            `/integration-sync/${systemKey}/overview`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-get-integration-overview'),
                    502: __('error-get-integration-overview'),
                },
            })
        )
        .then((response) => response.data);
};

export const enqueueIntegrationManualSync = async (
    systemKey: string,
    request: RequestManualSync
): Promise<ManualSyncResult> => {
    return http
        .backoffice()
        .post<ManualSyncResult>(
            `/integration-sync/${systemKey}`,
            request,
            useCustomConfig({
                errorMessage: {
                    404: __('error-enqueue-integration-manual-sync-not-found'),
                    500: __('error-enqueue-integration-manual-sync'),
                    502: __('error-enqueue-integration-manual-sync'),
                },
            })
        )
        .then((response) => response.data);
};

export const getIntegrationSyncJobs = async (systemKey: string): Promise<SyncJob[]> => {
    return http
        .backoffice()
        .get<SyncJob[]>(
            `/integration-sync/${systemKey}/jobs`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-sync-jobs'),
                    502: __('error-load-sync-jobs'),
                },
            })
        )
        .then((response) => response.data);
};

export const getIntegrationConfigs = async (
    tenantId: string
): Promise<ResponseGetIntegrationConfigs> => {
    return http
        .backoffice()
        .get<ResponseGetIntegrationConfigs>(
            `/integration-config/${tenantId}`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-get-integration-configs'),
                    502: __('error-get-integration-configs'),
                },
            })
        )
        .then((response) => response.data);
};

export const getIntegrationConfig = async (
    tenantId: string,
    systemKey: string
): Promise<IntegrationConfigValues> => {
    return http
        .backoffice()
        .get<IntegrationConfigValues>(
            `/integration-config/${tenantId}/${systemKey}`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-get-integration-config'),
                    502: __('error-get-integration-config'),
                },
            })
        )
        .then((response) => response.data);
};

export const saveIntegrationConfig = async (
    request: RequestSaveIntegrationConfig
): Promise<void> => {
    return http
        .backoffice()
        .put(
            `/integration-config/${request.tenantId}/${request.systemKey}`,
            { fieldValues: request.fieldValues },
            useCustomConfig({
                errorMessage: {
                    400: __('error-save-integration-config'),
                    500: __('error-save-integration-config'),
                    502: __('error-save-integration-config'),
                },
            })
        )
        .then(() => undefined);
};

export const deleteIntegrationConfig = async (
    tenantId: string,
    systemKey: string
): Promise<void> => {
    return http
        .backoffice()
        .delete(
            `/integration-config/${tenantId}/${systemKey}`,
            useCustomConfig({
                errorMessage: {
                    404: __('error-delete-integration-config'),
                    500: __('error-delete-integration-config'),
                    502: __('error-delete-integration-config'),
                },
            })
        )
        .then(() => undefined);
};

export const deleteTenant = async (tenantId: string): Promise<void> => {
    return http
        .backoffice()
        .delete(
            `/tenants/${tenantId}`,
            useCustomConfig({
                errorMessage: {
                    400: __('error-delete-tenant'),
                    404: __('error-delete-tenant'),
                    500: __('error-delete-tenant'),
                    502: __('error-delete-tenant'),
                },
            })
        )
        .then(() => undefined);
};

export const createTenant = async (request: RequestCreateTenant): Promise<ResponseCreateTenant> => {
    return http
        .backoffice()
        .post<ResponseCreateTenant>(
            '/tenants',
            request,
            useCustomConfig({
                errorMessage: {
                    400: __('error-create-tenant'),
                    422: __('error-create-tenant'),
                    500: __('error-create-tenant'),
                    502: __('error-create-tenant'),
                },
            })
        )
        .then((response) => response.data);
};

export const seedAllTenantData = async (): Promise<DataSeedingResponse> => {
    return http
        .backoffice()
        .post<DataSeedingResponse>(
            '/data-seeding/seed-all-tenant-data',
            null,
            useCustomConfig({
                errorMessage: {
                    500: __('error-seed-all-tenant-data'),
                    502: __('error-seed-all-tenant-data'),
                },
            })
        )
        .then((response) => response.data);
};

export const getExternalSystemsForTenant = async (
    tenantId: string
): Promise<TenantExternalSystem[]> => {
    return http
        .backoffice()
        .get<TenantExternalSystem[]>(
            `/key-indicate/tenants/${tenantId}/external-systems`,
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-external-systems-for-tenant'),
                    502: __('error-load-external-systems-for-tenant'),
                },
            })
        )
        .then((response) => response.data);
};

export const syncRawDataProcessor = async (
    body: RequestSyncRawDataProcessor
): Promise<{ id: string }> => {
    return http
        .backoffice()
        .post<{ id: number }>(
            '/key-indicate/sync-raw-data-processor',
            body,
            useCustomConfig({
                errorMessage: {
                    404: __('error-sync-raw-data-processor-not-found'),
                    500: __('error-sync-raw-data-processor'),
                    502: __('error-sync-raw-data-processor'),
                },
            })
        )
        .then((response) => ({ id: String(response.data.id) }));
};

export const syncCoachingData = async (body: RequestSyncCoachingData): Promise<{ id: string }> => {
    return http
        .backoffice()
        .post<{ id: number }>(
            '/key-indicate/sync-coaching-data',
            body,
            useCustomConfig({
                errorMessage: {
                    404: __('error-sync-raw-data-processor-not-found'),
                    500: __('error-sync-raw-data-processor'),
                    502: __('error-sync-raw-data-processor'),
                },
            })
        )
        .then((response) => ({ id: String(response.data.id) }));
};

export const syncAiInsightsData = async (
    body: RequestSyncAiInsightsData
): Promise<{ id: string }> => {
    return http
        .backoffice()
        .post<{ id: number }>(
            '/key-indicate/sync-asr-data-ims',
            body,
            useCustomConfig({
                errorMessage: {
                    404: __('error-sync-ai-insights-data-not-found'),
                    500: __('error-sync-ai-insights-data'),
                    502: __('error-sync-ai-insights-data'),
                },
            })
        )
        .then((response) => ({ id: String(response.data.id) }));
};

export const getSyncJobs = async (jobName: string): Promise<SyncJob[]> => {
    return http
        .backoffice()
        .get<SyncJob[]>(
            '/key-indicate/sync-jobs',
            useCustomConfig({
                params: { name: jobName },
                errorMessage: {
                    500: __('error-load-sync-jobs'),
                    502: __('error-load-sync-jobs'),
                },
            })
        )
        .then((response) => response.data);
};

export const getCsmUsers = async (): Promise<ResponseGetTenantUsers> => {
    return http
        .backoffice()
        .get<ResponseGetTenantUsers>(
            '/tenant/users/csm',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-csm-users'),
                    502: __('error-load-csm-users'),
                },
            })
        )
        .then((response) => response.data);
};

export const getSysOpUsers = async (): Promise<ResponseGetSysOpUsers> => {
    return http
        .backoffice()
        .get<ResponseGetSysOpUsers>(
            '/tenant/users/sysop',
            useCustomConfig({
                errorMessage: {
                    500: __('error-load-sysop-users'),
                    502: __('error-load-sysop-users'),
                },
            })
        )
        .then((response) => response.data);
};

export const createSysOpUser = async (request: RequestCreateSysOpUser): Promise<ApiTenantUser> => {
    return http
        .backoffice()
        .post<ApiTenantUser>(
            `/tenant/users/${request.tenantId}`,
            {
                email: request.email,
                firstName: request.firstName,
                lastName: request.lastName,
                roleId: request.roleId,
                // Trigger the Keycloak account-setup email.
                sendAccountSetupEmail: true,
            },
            // silent: the mutation's onError shows the backend's actual `detail` message
            // (e.g. "already exists in Keycloak") instead of this interceptor's generic toast.
            useCustomConfig({ silent: true })
        )
        .then((response) => response.data);
};

export const changeUserRole = async (request: RequestChangeUserRole): Promise<ApiTenantUser> => {
    return http
        .backoffice()
        .put<ApiTenantUser>(
            `/tenant/users/${request.tenantId}/${request.userId}/role`,
            { roleId: request.roleId },
            useCustomConfig({ silent: true })
        )
        .then((response) => response.data);
};

export const updateOrgTree = async (
    request: RequestUpdateOrgTree
): Promise<ResponseUpdateOrgTree> => {
    return http
        .backoffice()
        .post<ResponseUpdateOrgTree>(
            '/key-indicate/org-tree',
            request,
            useCustomConfig({
                errorMessage: {
                    400: __('error-update-org-tree'),
                    404: __('error-update-org-tree'),
                    500: __('error-update-org-tree'),
                    502: __('error-update-org-tree'),
                },
            })
        )
        .then((response) => response.data);
};
